import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import Loader from "../components/utils/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../layouts/MainLayout'

// Generate a simple math captcha
const generateCaptcha = () => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { a, b, answer: a + b };
};

const ResetPassword = () => {
  const [emailId, setEmailId] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);
  
  const [fetchData, { loading }] = useFetch();

  // Refresh captcha on mount
  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);

  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const fieldError = (field) =>
    formErrors[field] ? (
      <p className="mt-1 !text-pink-500 text-xs flex items-center gap-1">
        <i className="fa-solid fa-circle-exclamation"></i>
        {formErrors[field]}
      </p>
    ) : null;

  const validate = () => {
    const errors = {};

    if (!emailId) {
      errors.emailId = "Email is required.";
    } else if (!isValidEmail(emailId)) {
      errors.emailId = "Enter a valid email address.";
    }

    if (!recoveryToken) {
      errors.recoveryToken = "Recovery Token is required.";
    } else if (recoveryToken.length !== 6) {
      errors.recoveryToken = "Recovery Token must be exactly 6 characters.";
    }

    if (!newPassword) {
      errors.newPassword = "New password is required.";
    } else if (newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8characters.";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password.";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (!captchaInput) {
      errors.captcha = "Please solve the captcha.";
    } else if (parseInt(captchaInput, 10) !== captcha.answer) {
      errors.captcha = "Incorrect answer. Try again.";
      refreshCaptcha();
    }

    return errors;
  };

  const handleSubmit = useCallback(() => {
    setSuccessMessage("");
    setErrorMessage("");

    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const config = {
      url: "/auth/resetpassword",
      method: "post",
      data: {
        email: emailId,
        recoveryToken: recoveryToken,
        newPassword,
      },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        setSuccessMessage(response?.msg || "Password updated successfully.");
        // Clear sensitive fields
        setEmailId("");
        setNewPassword("");
        setConfirmPassword("");
        setRecoveryToken("");
        setCaptchaInput("");
        refreshCaptcha();
      })
      .catch((error) => {
        setErrorMessage(
          error?.response?.data?.msg ||
            error?.message ||
            "Unable to reset password. Please try again."
        );
        refreshCaptcha();
      });
  }, [fetchData, emailId, recoveryToken, newPassword, confirmPassword, captchaInput, captcha]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleTokenVisibility = () => {
    setShowToken(!showToken);
  };

  return (
    <MainLayout>
      <div className="flex justify-center items-center min-h-screen">
      <div className="flex flex-col bg-gray-600 text-white p-6 w-full max-w-[500px] border-2 shadow-md rounded-md">
        {loading ? (
          <Loader />
        ) : (
          <>
            <h2 className="text-center mb-1 text-yellow-400 text-lg font-semibold">
              Reset Your Password
            </h2>
            <p className="text-center text-gray-300 text-xs mb-5">
              Enter your email, recovery token, and a new password.
            </p>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col w-full">
                <label
                  htmlFor="emailId"
                  className="text-sm after:content-['*'] after:ml-0.5 after:text-red-400"
                >
                  Email
                </label>
                <input
                  autoComplete="off"
                  type="text"
                  name="emailId"
                  id="emailId"
                  className="w-full px-4 h-10 !text-black border-lime-100 rounded-lg text-xs lg:text-sm"
                  style={{ width: "100%", display: "block", boxSizing: "border-box" }}
                  value={emailId}
                  placeholder="youremail@domain.com"
                  onChange={(e) => setEmailId(e.target.value)}
                />
                {fieldError("emailId")}
                <div className="w-full">
                  <label
                    htmlFor="recoveryToken"
                    className="block text-sm after:content-['*'] after:ml-0.5 after:text-red-400 mb-1"
                  >
                    Input Recovery Token You Have
                  </label>

                  <div className="relative">
                    <input
                      autoComplete="new-password"
                      type={showToken ? "text" : "password"}
                      name="recoveryToken"
                      id="recoveryToken"
                      className="w-full h-10 px-4 pr-10 text-xs leading-none text-black border border-gray-300 rounded-lg"
                      value={recoveryToken}
                      placeholder="Min. 6 characters"
                      onChange={(e) => setRecoveryToken(e.target.value)}
                    />

                    <button
                      type="button"
                      onClick={toggleTokenVisibility}
                      className="absolute inset-y-0 right-3 flex items-center text-blue-700 w-fit"
                    >
                      <FontAwesomeIcon
                        icon={showToken ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>

                  {fieldError("recoveryToken")}
                </div>
                <div className="w-full">
                  <label
                    htmlFor="newPassword"
                    className="block text-sm after:content-['*'] after:ml-0.5 after:text-red-400 mb-1"
                  >
                    New Password
                  </label>

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="newPassword"
                      id="newPassword"
                      className="w-full h-10 px-4 pr-10 text-xs leading-none text-black border border-gray-300 rounded-lg"
                      value={newPassword}
                      placeholder="Min. 8 characters"
                      onChange={(e) => setNewPassword(e.target.value)}
                    />

                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-3 flex items-center text-blue-700 w-fit"
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>

                  {fieldError("newPassword")}
                </div>
                <label
                  htmlFor="confirmPassword"
                  className="text-sm after:content-['*'] after:ml-0.5 after:text-red-400"
                >
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  value={confirmPassword}
                  className="w-full h-10 px-4 pr-10 text-xs leading-none text-black border border-gray-300 rounded-lg"
                  placeholder="Re-enter your new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {fieldError("confirmPassword")}
              </div>

              {/* Math Captcha */}
              <div>
                <label className="text-sm after:content-['*'] after:ml-0.5 after:text-red-400">
                  Security Check
                </label>
                <div className="flex flex-row items-center gap-2 mt-1">
                  <span className="bg-gray-700 px-3 py-1.5 rounded text-yellow-300 font-mono text-sm select-none">
                    {captcha.a} + {captcha.b} = ?
                  </span>
                  <input
                    type="number"
                    name="captcha"
                    id="captcha"
                    className="w-20 p-1 !text-black border-lime-100 rounded-lg"
                    value={captchaInput}
                    placeholder="Answer"
                    onChange={(e) => setCaptchaInput(e.target.value)}
                  />
                  <button
                    type="button"
                    title="Refresh captcha"
                    onClick={refreshCaptcha}
                    className="text-gray-300 hover:text-yellow-400 text-xs w-6 p-1 px-1"
                  >
                    <i className="fa-solid fa-rotate-right"></i>
                  </button>
                </div>
                {fieldError("captcha")}
              </div>
            </div>

            {/* Submit */}
            <button
              type="button"
              className="mt-5 w-full bg-primary text-white text-sm px-4 py-2 font-medium hover:bg-primary-dark rounded"
              onClick={handleSubmit}
            >
              Reset Password
            </button>

            {/* Feedback */}
            {errorMessage && (
              <div className="text-xs mt-3 p-2 rounded bg-red-100 text-red-700">
                <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="text-xs mt-3 p-2 rounded bg-green-100 text-green-700">
                <i className="fa-solid fa-circle-check mr-1"></i>
                {successMessage}
              </div>
            )}

            <div className="flex flex-col justify-center items-center gap-1 mt-4">
              <Link to="/login" className="flex justify-center items-center text-blue-800 bg-yellow-200 hover:bg-yellow-300 hover:text-blue-900 text-sm w-full py-1 text-center border rounded-lg">
                Remembered your password? Login here
              </Link>
            </div>
          </>
        )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ResetPassword;
